import {useEffect, useMemo, useState} from 'react';
import {Search, TriangleAlert} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Switch,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import type {CustomerTaxonomy, TaxonomyChange} from '../../api/workflows';
import {TaxonomyChanges} from './TaxonomyChanges';
import {fuzzyMatch, label, short, subtree, type ConfigDraft, type TaxonomyNode} from './workflowUi';

function depthOf(node: TaxonomyNode, byId: Map<string, TaxonomyNode>) {
  let depth = 0;
  let parent = node.parent_concept_id ? byId.get(node.parent_concept_id) : undefined;
  while (parent) {
    depth++;
    parent = parent.parent_concept_id ? byId.get(parent.parent_concept_id) : undefined;
  }

  return depth;
}

function pathOf(node: TaxonomyNode, byId: Map<string, TaxonomyNode>) {
  const parts = [label(node)];
  let parent = node.parent_concept_id ? byId.get(node.parent_concept_id) : undefined;
  while (parent) {
    parts.unshift(label(parent));
    parent = parent.parent_concept_id ? byId.get(parent.parent_concept_id) : undefined;
  }

  return parts.join(' / ');
}

function ConceptPicker({
  taxonomy,
  value,
  onChange
}: {
  readonly taxonomy: CustomerTaxonomy;
  readonly value: readonly string[];
  readonly onChange: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const byId = useMemo(
    () => new Map(taxonomy.nodes.map((node) => [node.concept_id, node])),
    [taxonomy.nodes]
  );
  const searching = Boolean(query.trim());
  const list = useMemo(
    () =>
      searching
        ? taxonomy.nodes
            .filter((node) => fuzzyMatch(`${node.name} ${label(node)}`, query))
            .slice(0, 120)
        : taxonomy.nodes.filter((node) => depthOf(node, byId) <= 1),
    [taxonomy.nodes, byId, query, searching]
  );
  const current = value.flatMap((id) => byId.get(id) ?? []);
  const size = current.reduce(
    (total, node) => total + subtree(taxonomy.nodes, node.concept_id).length,
    0
  );

  function toggle(id: string) {
    if (value.includes(id)) {
      onChange(value.filter((other) => other !== id));
      return;
    }

    const within = new Set(subtree(taxonomy.nodes, id).map((node) => node.concept_id));
    const kept = value.filter(
      (other) =>
        !within.has(other) && !subtree(taxonomy.nodes, other).some((node) => node.concept_id === id)
    );
    onChange([...kept, id]);
  }

  return (
    <div className="border-border bg-card flex flex-col overflow-hidden rounded-md border">
      <div className="border-border flex items-center gap-2 border-b px-3 py-2">
        <Search aria-hidden className="text-muted-foreground size-4 shrink-0" />
        <Input
          aria-label="Root concepts"
          className="h-8 border-0 shadow-none focus-visible:ring-0"
          placeholder="Search concepts or paths"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </div>
      <div role="group" aria-label="Root concept options" className="max-h-56 overflow-auto py-1">
        {list.map((node) => {
          const selected = value.includes(node.concept_id);
          const depth = depthOf(node, byId);
          return (
            <button
              key={node.concept_id}
              type="button"
              role="checkbox"
              aria-checked={selected}
              className={`hover:bg-muted focus-visible:outline-ring flex min-h-8 w-full items-center gap-2 px-2.5 py-1 text-left text-xs focus-visible:outline-2 focus-visible:-outline-offset-2 ${selected ? 'bg-accent' : ''}`}
              style={{paddingLeft: searching ? 10 : 10 + depth * 18}}
              onClick={() => {
                toggle(node.concept_id);
              }}>
              <span
                aria-hidden
                className={`size-3.5 shrink-0 rounded-sm ring-1 ring-inset ${selected ? 'ring-primary border-primary border-4' : 'ring-muted-foreground'}`}
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className={`truncate ${depth === 0 && !searching ? 'font-medium' : ''}`}>
                  {label(node)}
                </span>
                {searching && depth > 0 ? (
                  <span className="text-muted-foreground truncate">
                    {pathOf(node, byId).split(' / ').slice(0, -1).join(' / ')}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
        {list.length === 0 ? (
          <p className="text-muted-foreground m-0 px-3 py-2.5 text-xs">No concepts match.</p>
        ) : null}
      </div>
      <div className="border-border bg-muted text-muted-foreground border-t px-2.5 py-2 text-xs">
        {current.length > 0 ? (
          <>
            Scope:{' '}
            <b className="text-foreground font-medium">
              {current.map((node) => pathOf(node, byId)).join('; ')}
            </b>{' '}
            and everything under {current.length === 1 ? 'it' : 'them'} · {size} concepts
          </>
        ) : (
          'Pick the concepts this guidance covers.'
        )}
      </div>
    </div>
  );
}

export function ConfigureDialog({
  open,
  busy,
  first,
  workflowKey,
  revision,
  taxonomy,
  initial,
  outsideScopeCount,
  taxonomyChanges,
  onClose,
  onSubmit
}: {
  readonly open: boolean;
  readonly busy: boolean;
  readonly first: boolean;
  readonly workflowKey: string;
  readonly revision: string | null;
  readonly taxonomy: CustomerTaxonomy;
  readonly initial: ConfigDraft;
  readonly outsideScopeCount: number;
  readonly taxonomyChanges: readonly TaxonomyChange[];
  readonly onClose: () => void;
  readonly onSubmit: (value: ConfigDraft) => void;
}) {
  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (open) {
      setForm(initial);
    }
    // Reset only when the dialog opens, not when the parent re-renders `initial`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = <K extends keyof ConfigDraft>(key: K, value: ConfigDraft[K]) => {
    setForm((current) => ({...current, [key]: value}));
  };

  const keywords = [
    ...new Set(
      form.routing_keywords
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  ];
  const valid =
    Boolean(form.name.trim() && form.roots.length > 0 && taxonomy.pack_version) &&
    outsideScopeCount === 0 &&
    !busy;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{first ? 'Configure guidance' : 'Edit configuration'}</DialogTitle>
          <DialogDescription>
            {first ? (
              'Set the scope and routing. Another approver has to approve it before RD routes to this guidance.'
            ) : (
              <>
                Goes to an approver. RD keeps serving revision{' '}
                <code className="bg-muted rounded px-1 font-mono text-xs">{short(revision)}</code>{' '}
                until it’s approved.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="workflow-config-name">Name</FieldLabel>
                <Input
                  id="workflow-config-name"
                  maxLength={256}
                  value={form.name}
                  onChange={(event) => {
                    set('name', event.target.value);
                  }}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="workflow-config-key">Key</FieldLabel>
                <Input
                  disabled
                  id="workflow-config-key"
                  className="font-mono"
                  value={workflowKey}
                />
              </Field>
            </div>
            <Field>
              <FieldLabel>Scope</FieldLabel>
              <FieldDescription>
                The root concepts in the customer taxonomy
                {taxonomy.pack_version ? ` · ${taxonomy.pack_version}` : ''}. The router can only
                assign concepts under them.
              </FieldDescription>
              <ConceptPicker
                taxonomy={taxonomy}
                value={form.roots}
                onChange={(ids) => {
                  set('roots', ids);
                }}
              />
            </Field>
            {outsideScopeCount > 0 ? (
              <Alert variant="destructive">
                <TriangleAlert aria-hidden />
                <AlertTitle>
                  {outsideScopeCount} grounding{' '}
                  {outsideScopeCount === 1 ? 'entry falls' : 'entries fall'} outside this scope
                </AlertTitle>
                <AlertDescription>
                  Retire or move {outsideScopeCount === 1 ? 'it' : 'them'} in Grounding first, then
                  narrow the scope.
                </AlertDescription>
              </Alert>
            ) : null}
            {!first && taxonomyChanges.length > 0 ? (
              <Field>
                <FieldLabel>Taxonomy changes</FieldLabel>
                <FieldDescription>
                  Since revision {short(revision)} was approved. Approving carries grounding on a
                  superseded concept over to its replacement.
                </FieldDescription>
                <TaxonomyChanges changes={taxonomyChanges} />
              </Field>
            ) : null}
            <Field>
              <FieldLabel htmlFor="workflow-config-definition">Routing definition</FieldLabel>
              <Textarea
                id="workflow-config-definition"
                rows={3}
                value={form.routing_definition}
                onChange={(event) => {
                  set('routing_definition', event.target.value);
                }}
              />
              <FieldDescription>
                The router reads this to decide whether an item belongs in this guidance at all.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="workflow-config-keywords">Routing keywords</FieldLabel>
              <Input
                id="workflow-config-keywords"
                placeholder="Comma-separated"
                value={form.routing_keywords}
                onChange={(event) => {
                  set('routing_keywords', event.target.value);
                }}
              />
              {keywords.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {keywords.map((keyword) => (
                    <span key={keyword} className="bg-muted rounded-full px-2.5 py-0.5 text-xs">
                      {keyword}
                    </span>
                  ))}
                </div>
              ) : null}
            </Field>
            <label className="inline-flex cursor-pointer items-center gap-2.5">
              <Switch
                checked={form.enabled}
                aria-label="Enabled"
                onCheckedChange={(value) => {
                  set('enabled', value);
                }}
              />
              <span className="flex flex-col">
                <span className="font-medium">Enabled</span>
                <span className="text-muted-foreground text-xs">
                  When off, the router skips this guidance but keeps its configuration.
                </span>
              </span>
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2.5">
              <Switch
                checked={form.include_non_violative}
                aria-label="Always offer Non-Violative concepts"
                onCheckedChange={(value) => {
                  set('include_non_violative', value);
                }}
              />
              <span className="flex flex-col">
                <span className="font-medium">Always offer Non-Violative concepts</span>
                <span className="text-muted-foreground text-xs">
                  Every Non-Violative concept in this workflow&apos;s taxonomy is offered alongside
                  the routed ones, so the model can rule content out.
                </span>
              </span>
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!valid}
            onClick={() => {
              onSubmit({...form, name: form.name.trim()});
            }}>
            Submit for approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
